import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Settings2, Check, Trash2, GripVertical } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getGallery, uploadGalleryImage, deleteGalleryImage, reorderGallery, resolveMedia, apiError } from "../lib/api";
import Gallery from "./Gallery";

export default function ProjectGallery({ galleryKey, fallback = [], label }) {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [images, setImages] = useState(null);
  const [busy, setBusy] = useState(false);
  const [managing, setManaging] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const inputRef = useRef(null);

  const load = () => getGallery(galleryKey).then((d) => setImages(d.images)).catch(() => setImages([]));
  useEffect(() => { load(); }, [galleryKey]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) await uploadGalleryImage(galleryKey, f);
      toast.success(files.length > 1 ? "Photos uploaded" : "Photo uploaded");
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const del = async (id) => {
    try { await deleteGalleryImage(galleryKey, id); setImages((imgs) => imgs.filter((i) => i.id !== id)); toast.success("Photo removed"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const onDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const next = [...images];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setImages(next);
    setDragIdx(null);
    try { await reorderGallery(galleryKey, next.map((i) => i.id)); }
    catch (err) { toast.error(apiError(err)); load(); }
  };

  const list = images === null ? fallback.map((u, i) => ({ id: `f${i}`, url: u })) : images;

  return (
    <div>
      {isAdmin && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple hidden onChange={onFiles} data-testid={`gallery-upload-input-${galleryKey}`} />
          <button onClick={() => inputRef.current?.click()} disabled={busy} data-testid={`gallery-upload-${galleryKey}`} className="inline-flex items-center gap-2 rounded-full bg-nead-navy px-5 py-2.5 text-sm font-semibold text-white hover:bg-nead-pink transition-colors disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Uploading…" : "Upload photos"}
          </button>
          {images && images.length > 0 && (
            <button onClick={() => setManaging((v) => !v)} data-testid={`gallery-manage-${galleryKey}`} className="inline-flex items-center gap-2 rounded-full border-2 border-nead-navy/20 px-5 py-2.5 text-sm font-semibold text-nead-navy hover:border-nead-pink hover:text-nead-pink transition-colors">
              {managing ? <><Check className="h-4 w-4" /> Done</> : <><Settings2 className="h-4 w-4" /> Manage photos</>}
            </button>
          )}
          {managing && <span className="text-sm text-nead-navy/50">Drag to reorder, or remove photos</span>}
        </div>
      )}

      {managing ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {list.map((im, idx) => (
            <div
              key={im.id}
              draggable
              onDragStart={() => setDragIdx(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(idx)}
              data-testid={`gallery-manage-item-${im.id}`}
              className={`relative aspect-square overflow-hidden rounded-xl border-2 ${dragIdx === idx ? "border-nead-pink opacity-50" : "border-transparent"} cursor-grab active:cursor-grabbing`}
            >
              <img src={resolveMedia(im.url)} alt="" className="h-full w-full object-cover pointer-events-none" />
              <div className="absolute top-1.5 left-1.5 rounded-md bg-nead-ink/70 p-1 text-white"><GripVertical className="h-3.5 w-3.5" /></div>
              <button onClick={() => del(im.id)} data-testid={`gallery-delete-${im.id}`} className="absolute top-1.5 right-1.5 rounded-full bg-red-500 p-1.5 text-white hover:bg-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      ) : (
        <Gallery images={list.map((im) => resolveMedia(im.url))} label={label} />
      )}
    </div>
  );
}
